<script setup lang="ts">
import { computed } from 'vue';
import { useRouter } from 'vue-router';
import { useI18n } from 'vue-i18n';
import { ArrowRight, ArrowLeft } from 'lucide-vue-next';
import OAuthButtons from './OAuthButtons.vue';
import { authAPI } from '../services/api';
import { useAuthState } from '../composables/useAuthState';
import SignUpStepAccount from './auth/SignUpStepAccount.vue';
import SignUpStepProfile from './auth/SignUpStepProfile.vue';
import SignUpStepCompany from './auth/SignUpStepCompany.vue';

const router = useRouter();
const { t } = useI18n();
const { formData, currentStep, isLoading, error, updateFormData, persistEmail } = useAuthState();

const TOTAL_STEPS = 3;

const isStepValid = computed(() => {
  switch (currentStep.value) {
    case 1:
      return (
        formData.value.email &&
        formData.value.password.length >= 8 &&
        formData.value.password === formData.value.passwordConfirmation
      );
    case 2:
      return formData.value.firstName && formData.value.lastName;
    case 3:
      return formData.value.company;
    default:
      return false;
  }
});

const getButtonText = () => {
  if (isLoading.value) return t('signup.loading');
  switch (currentStep.value) {
    case 1:
      return t('signup.continue');
    case 2:
      return t('signup.continue');
    case 3:
      return t('signup.create_account');
    default:
      return t('signup.continue');
  }
};

const nextStep = () => {
  if (currentStep.value < TOTAL_STEPS) {
    currentStep.value++;
  }
};

const prevStep = () => {
  if (currentStep.value > 1) {
    currentStep.value--;
  }
};

const handleRegister = async () => {
  isLoading.value = true;
  error.value = null;

  try {
    await authAPI.register({
      first_name: formData.value.firstName,
      last_name: formData.value.lastName,
      email: formData.value.email,
      password: formData.value.password,
      password_confirmation: formData.value.passwordConfirmation,
      organization_name: formData.value.company,
    });

    persistEmail();
    router.push('/activate');
  } catch (err: any) {
    error.value = err.response?.data?.message || t('signup.error_registration_failed');
    console.error('Registration error:', err);
  } finally {
    isLoading.value = false;
  }
};

const handleSubmit = (e: Event) => {
  e.preventDefault();

  if (currentStep.value === 3) {
    handleRegister();
  } else {
    nextStep();
  }
};
</script>

<template>
  <div class="w-full bg-[#111113] rounded-3xl shadow-sm border border-[#2A2018] p-8 md:p-10">
    <!-- Header -->
    <div class="mb-10">
      <h1 class="text-2xl text-[#F5F0EA] mb-2">{{ $t('signup.title') }}</h1>
      <p class="text-[#9E9080]">{{ $t('signup.subtitle') }}</p>
    </div>

    <!-- Progress Indicator -->
    <div class="mb-10">
      <div class="flex items-center justify-between mb-3">
        <div v-for="step in [1, 2, 3]" :key="step" class="flex items-center flex-1">
          <div
            :class="[
              'w-9 h-9 rounded-full flex items-center justify-center text-sm transition-all',
              step <= currentStep ? 'bg-[#D4956A] text-[#0A0A0B]' : 'bg-[#18181C] text-[#9E9080]',
            ]"
          >
            <span>{{ step }}</span>
          </div>
          <div
            v-if="step < 3"
            :class="[
              'flex-1 h-0.5 mx-3 transition-colors',
              step < currentStep ? 'bg-[#D4956A]' : 'bg-[#18181C]',
            ]"
          />
        </div>
      </div>
      <div class="flex justify-between text-xs text-[#9E9080]">
        <span :class="currentStep === 1 ? 'text-[#D4956A]' : ''">{{
          $t('signup.step_account')
        }}</span>
        <span :class="currentStep === 2 ? 'text-[#D4956A]' : ''">{{
          $t('signup.step_profile')
        }}</span>
        <span :class="currentStep === 3 ? 'text-[#D4956A]' : ''">{{
          $t('signup.step_company')
        }}</span>
      </div>
    </div>

    <!-- OAuth Buttons - Only on Step 1 -->
    <div v-if="currentStep === 1" class="mb-8">
      <OAuthButtons />
      <div class="relative my-8">
        <div class="absolute inset-0 flex items-center">
          <div class="w-full border-t border-[#2A2018]" />
        </div>
        <div class="relative flex justify-center text-sm">
          <span class="px-3 bg-[#111113] text-[#524840]">{{ $t('signup.or') }}</span>
        </div>
      </div>
    </div>

    <!-- Form Steps -->
    <form @submit="handleSubmit">
      <Transition
        mode="out-in"
        enter-active-class="transition-all duration-200"
        enter-from-class="opacity-0 translate-x-2"
        enter-to-class="opacity-100 translate-x-0"
        leave-active-class="transition-all duration-200"
        leave-from-class="opacity-100 translate-x-0"
        leave-to-class="opacity-0 -translate-x-2"
      >
        <SignUpStepAccount
          v-if="currentStep === 1"
          :email="formData.email"
          :password="formData.password"
          :password-confirmation="formData.passwordConfirmation"
          :error="error"
          @update:email="updateFormData('email', $event)"
          @update:password="updateFormData('password', $event)"
          @update:password-confirmation="updateFormData('passwordConfirmation', $event)"
        />

        <SignUpStepProfile
          v-else-if="currentStep === 2"
          :first-name="formData.firstName"
          :last-name="formData.lastName"
          @update:first-name="updateFormData('firstName', $event)"
          @update:last-name="updateFormData('lastName', $event)"
        />

        <SignUpStepCompany v-else-if="currentStep === 3" :company="formData.company" />
      </Transition>

      <!-- Navigation Buttons -->
      <div class="flex gap-3 mt-8">
        <button
          v-if="currentStep > 1"
          type="button"
          @click="prevStep"
          class="flex items-center justify-center gap-2 px-5 py-3 bg-[#18181C] rounded-2xl hover:bg-[#1E1E23] transition text-[#9E9080]"
        >
          <ArrowLeft class="w-4 h-4" />
          {{ $t('signup.back') }}
        </button>
        <button
          type="submit"
          :disabled="!isStepValid || isLoading"
          class="flex-1 flex items-center justify-center gap-2 px-5 py-3 bg-[#D4956A] text-[#0A0A0B] rounded-2xl hover:bg-[#E8B48A] disabled:bg-[#18181C] disabled:text-[#524840] disabled:cursor-not-allowed transition font-medium"
        >
          <span
            v-if="isLoading"
            class="w-4 h-4 border-2 border-current border-t-transparent rounded-full animate-spin"
          ></span>
          {{ getButtonText() }}
          <ArrowRight v-if="currentStep < 3" class="w-4 h-4" />
        </button>
      </div>
    </form>

    <!-- Sign In Link -->
    <p class="mt-8 text-center text-sm text-[#9E9080]">
      {{ $t('signup.already_have_account') }}
      <a href="#" class="text-[#D4956A] hover:underline">{{ $t('signup.sign_in') }}</a>
    </p>
  </div>
</template>
